import { AUTH_TYPES, TEMPLATE_CATEGORIES } from './constants.js'

/*
 * Freshdesk ticket notifier for privacy request events.
 *
 * POSTs to /api/v2/tickets on the tenant's Freshdesk domain. Auth follows the framework's single-
 * auth-per-system constraint: the no-auth type is selected and the admin pastes a static
 * Authorization: Basic <base64(apikey:X)> custom header (Freshdesk uses the API key as the username
 * with any password). priority uses Freshdesk's 1 (Low) - 4 (Urgent) scale; status 2 is Open.
 */
export const freshdeskTemplate = {
	id: 'freshdesk',
	guid: 'adb7e493-3d1c-4e01-a384-6d898a4ea554',
	name: 'Freshdesk',
	category: TEMPLATE_CATEGORIES.SUPPORT,
	popularity: 'Popular',
	color: '#25c16f',
	icon: 'mdi-headset',
	description: 'Create Freshdesk tickets for privacy request activity, from new requests and data-subject messages through to deadline escalation.',
	features: ['Ticket Management', 'Priority Mapping', 'Deadline Escalation', 'Data Subject Requester'],
	endpoints: [
		{
			name: 'Create Ticket - New Privacy Request',
			url: 'https://[domain].freshdesk.com/api/v2/tickets',
			method: 'POST',
			description: 'Create a Freshdesk ticket when a new privacy request is submitted. Replace [domain] with your Freshdesk domain.',
			cassieObjectID: -13,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'RequestTypeName', filter: '' },
				{ name: 'StatusName', filter: '' },
				{ name: 'SubjectEmail', filter: '' }
			],
			requestBody: '{ "subject": "New Privacy Request [PLACEHOLDER_REQUESTNUMBER]", "description": "Privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]) of type [PLACEHOLDER_REQUESTTYPENAME] was submitted. Current status: [PLACEHOLDER_STATUSNAME].", "email": "[PLACEHOLDER_SUBJECTEMAIL]", "priority": 1, "status": 2 }',
			headers: [
				{ Name: 'Authorization', value: 'Basic [BASE64_APIKEY_X]' },
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 201, message: '' }]
		},
		{
			name: 'Create Ticket - Data Subject Message',
			url: 'https://[domain].freshdesk.com/api/v2/tickets',
			method: 'POST',
			description: 'Create a Freshdesk ticket when a data subject writes from the request status page.',
			cassieObjectID: -14,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'CommentText', filter: '' },
				{ name: 'SubjectEmail', filter: '' }
			],
			requestBody: '{ "subject": "New message on Privacy Request [PLACEHOLDER_REQUESTNUMBER]", "description": "New message from the data subject on privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]): [PLACEHOLDER_COMMENTTEXT]", "email": "[PLACEHOLDER_SUBJECTEMAIL]", "priority": 1, "status": 2 }',
			headers: [
				{ Name: 'Authorization', value: 'Basic [BASE64_APIKEY_X]' },
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 201, message: '' }]
		},
		{
			name: 'Create Ticket - Request/Task Nearing Deadline',
			url: 'https://[domain].freshdesk.com/api/v2/tickets',
			method: 'POST',
			description: 'Create a Freshdesk ticket when a privacy request or task enters its warning window.',
			cassieObjectID: -16,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'Deadline', filter: '' },
				{ name: 'DaysUntilDue', filter: '' },
				{ name: 'SubjectEmail', filter: '' }
			],
			requestBody: '{ "subject": "[PLACEHOLDER_EVENTTITLE] - Request [PLACEHOLDER_REQUESTNUMBER]", "description": "[PLACEHOLDER_EVENTTITLE] for privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]). Deadline [PLACEHOLDER_DEADLINE]; days until due: [PLACEHOLDER_DAYSUNTILDUE].", "email": "[PLACEHOLDER_SUBJECTEMAIL]", "priority": 2, "status": 2 }',
			headers: [
				{ Name: 'Authorization', value: 'Basic [BASE64_APIKEY_X]' },
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 201, message: '' }]
		},
		{
			name: 'Create Ticket - Deadline Breached',
			url: 'https://[domain].freshdesk.com/api/v2/tickets',
			method: 'POST',
			description: 'Create an urgent Freshdesk ticket when a privacy request or task passes its statutory deadline.',
			cassieObjectID: -17,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'EventTitle', filter: '' },
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'Deadline', filter: '' },
				{ name: 'DaysOverdue', filter: '' },
				{ name: 'SubjectEmail', filter: '' }
			],
			requestBody: '{ "subject": "[PLACEHOLDER_EVENTTITLE] - Request [PLACEHOLDER_REQUESTNUMBER]", "description": "[PLACEHOLDER_EVENTTITLE] for privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]). Deadline was [PLACEHOLDER_DEADLINE]; days overdue: [PLACEHOLDER_DAYSOVERDUE].", "email": "[PLACEHOLDER_SUBJECTEMAIL]", "priority": 4, "status": 2 }',
			headers: [
				{ Name: 'Authorization', value: 'Basic [BASE64_APIKEY_X]' },
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 201, message: '' }]
		},
		{
			name: 'Create Ticket - Inactive Request',
			url: 'https://[domain].freshdesk.com/api/v2/tickets',
			method: 'POST',
			description: 'Create a Freshdesk ticket when a privacy request has gone inactive.',
			cassieObjectID: -18,
			incrementField: 'ID',
			resetIncrementValue: false,
			fields: [
				{ name: 'RequestNumber', filter: '' },
				{ name: 'Reference', filter: '' },
				{ name: 'Reason', filter: '' },
				{ name: 'DaysSinceLastActivity', filter: '' },
				{ name: 'SubjectEmail', filter: '' }
			],
			requestBody: '{ "subject": "Inactive Privacy Request [PLACEHOLDER_REQUESTNUMBER]", "description": "Privacy request [PLACEHOLDER_REQUESTNUMBER] (reference [PLACEHOLDER_REFERENCE]) is inactive ([PLACEHOLDER_REASON]) - no activity for [PLACEHOLDER_DAYSSINCELASTACTIVITY] day(s).", "email": "[PLACEHOLDER_SUBJECTEMAIL]", "priority": 1, "status": 2 }',
			headers: [
				{ Name: 'Authorization', value: 'Basic [BASE64_APIKEY_X]' },
				{ Name: 'Content-Type', value: 'application/json' }
			],
			successResponses: [{ code: 201, message: '' }]
		}
	],
	defaults: {
		endpointAddress: 'https://[domain].freshdesk.com',
		authenticationTypeId: AUTH_TYPES.NO_AUTH,
		requestThrottlingWaitTimeMS: 1000,
		notes: 'Freshdesk tickets for privacy request events. No runtime auth is attached: replace [domain] and the Authorization header with Basic base64(apiKey:X), treated as a revocable credential. priority uses Freshdesk 1 (Low) to 4 (Urgent); status 2 is Open. The requester email is the data subject where present. Keep a non-zero throttle; the first sweep after deploy may burst a backlog of breached requests against Freshdesk per-minute rate limits.'
	}
}
